import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ClipboardList,
  Eye,
  Loader2,
  LogIn,
  MapPin,
  Package,
  User,
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';

/**
 * A job ticket, read from a link, by somebody who may not be signed in.
 *
 * The point of it is that it changes nothing. A supervisor on a phone, an owner
 * checking a job on the way home, a colleague picking up a shift — all of them
 * want to read what happened without an account and without the risk of
 * touching a live job.
 *
 * The one thing it *does* offer is a way through to filling the report, and it
 * asks the question that matters first: are you signed in? Someone who is goes
 * straight to the ticket. Someone who is not is told so before they tap,
 * rather than landing on a login form with no idea why.
 */
export function TicketReadOnlyPage() {
  const { token = '' } = useParams();

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['ticket-share', token],
    queryFn: () => api.sharedTicket(token),
    retry: false,
  });

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary-500" aria-label="Loading" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-sm px-5 py-20 text-center">
        <AlertCircle className="mx-auto h-10 w-10 text-danger-500" aria-hidden="true" />
        <h1 className="mt-4 font-heading text-xl">
          {(error as Error)?.message ?? 'Could not open this ticket'}
        </h1>
        <p className="mt-2 text-muted-foreground">
          Share links expire. Ask whoever sent it for a new one.
        </p>
      </div>
    );
  }

  const { ticket, materials, materialsOther, timeline, photoCount, viewer, reportPath } = data;

  /**
   * Where the report button goes.
   *
   * Signed in: straight to the ticket. Signed out: to sign in, carrying where
   * they were headed so they land on the ticket rather than the board and have
   * to find it again.
   */
  const reportHref = viewer.signedIn
    ? reportPath
    : `/?next=${encodeURIComponent(reportPath)}`;

  return (
    <div className="mx-auto min-h-screen max-w-2xl bg-background pb-16">
      <header className="gradient-hero px-5 py-6 text-white">
        <div className="flex items-center justify-between gap-3">
          <img src="/brand/logo.png" alt="GoSpeedNet" className="h-7 w-auto rounded bg-white p-1" />
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold">
            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
            Read only
          </span>
        </div>
        <h1 className="mt-4 font-heading text-2xl">{ticket.reference}</h1>
        <p className="mt-1 text-white/90">{ticket.title}</p>
        <p className="mt-2 text-sm text-white/70">
          {ticket.jobType} · {ticket.priority} · {ticket.status.replace(/_/g, ' ').toLowerCase()}
        </p>
      </header>

      <main className="space-y-4 px-5 py-5">
        {/*
          The way through to doing something about this job. Placed first,
          because somebody who opened this link to file their report should not
          have to scroll past a timeline to find it.
        */}
        <a href={reportHref} className="btn-primary w-full py-4 text-base">
          {viewer.signedIn ? (
            <>
              <ClipboardList className="h-5 w-5" aria-hidden="true" />
              Fill the job report
            </>
          ) : (
            <>
              <LogIn className="h-5 w-5" aria-hidden="true" />
              Sign in to fill the report
            </>
          )}
        </a>
        <p className="-mt-2 text-center text-xs text-muted-foreground">
          {viewer.signedIn
            ? `Signed in as ${viewer.name}. This opens the full ticket.`
            : 'You are not signed in. We will bring you back to this ticket afterwards.'}
        </p>

        <section className="card">
          <h2 className="font-heading text-lg">The job</h2>
          <dl className="mt-3 grid gap-3 sm:grid-cols-2">
            {[
              ['Status', ticket.status.replace(/_/g, ' ').toLowerCase()],
              ['Priority', ticket.priority],
              ['Technician', ticket.technician ?? 'Not assigned'],
              ['Customer', ticket.customer ?? '—'],
              ['Area', ticket.area ?? '—'],
              ['Equipment', ticket.element ?? '—'],
              ['Opened', new Date(ticket.createdAt).toLocaleString('en-KE')],
              [
                'Closed',
                ticket.closedAt ? new Date(ticket.closedAt).toLocaleString('en-KE') : 'Still open',
              ],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs label-mono tracking-[0.12em] text-muted-foreground">{label}</dt>
                <dd className="mt-0.5 font-medium capitalize">{value}</dd>
              </div>
            ))}
          </dl>

          {ticket.site && (
            <p className="mt-3 flex items-start gap-2 rounded-xl bg-muted p-3 text-sm">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              {ticket.site}
            </p>
          )}

          {ticket.description && (
            <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">
              {ticket.description}
            </p>
          )}
        </section>

        {(materials.length > 0 || materialsOther) && (
          <section className="card">
            <h2 className="flex items-center gap-2 font-heading text-lg">
              <Package className="h-5 w-5 text-primary-600" aria-hidden="true" />
              What was used
            </h2>
            <ul className="mt-3 space-y-1.5">
              {materials.map((line) => (
                <li
                  key={line.code}
                  className="flex items-baseline justify-between gap-3 rounded-xl bg-muted/60 px-3 py-2 text-sm"
                >
                  <span>
                    {line.code.replace(/_/g, ' ').toLowerCase()}
                    {line.serial && (
                      <span className="block text-xs text-muted-foreground">
                        serial {line.serial}
                      </span>
                    )}
                  </span>
                  <span className="font-semibold tabular-nums">{line.quantity}</span>
                </li>
              ))}
            </ul>
            {materialsOther && (
              <p className="mt-2 rounded-xl bg-muted/60 px-3 py-2 text-sm">{materialsOther}</p>
            )}
          </section>
        )}

        {timeline.length > 0 && (
          <section className="card">
            <h2 className="font-heading text-lg">What happened</h2>
            <ol className="mt-3 space-y-2">
              {timeline.map((entry, index) => (
                <li key={index} className="flex items-baseline gap-3 text-sm">
                  <span className="w-32 shrink-0 text-xs text-muted-foreground">
                    {new Date(entry.at).toLocaleString('en-KE')}
                  </span>
                  <span className="font-medium capitalize">
                    {entry.status?.replace(/_/g, ' ').toLowerCase()}
                  </span>
                  {entry.by && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <User className="h-3 w-3" aria-hidden="true" />
                      {entry.by}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}

        {/*
          Stated rather than silently absent. Somebody looking for the photos
          needs to know they exist and why they are not here, otherwise the
          page looks like it lost them.
        */}
        <p className="rounded-xl bg-muted p-3 text-sm text-muted-foreground">
          {photoCount > 0
            ? `${photoCount} ${photoCount === 1 ? 'photo is' : 'photos are'} on this job. They are not shown on a share link — a job photo is the inside of somebody's home or business. Sign in to see them.`
            : 'No photos on this job yet.'}
        </p>
      </main>
    </div>
  );
}
